import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ExternalLink, Plus, Search, Send, X } from "lucide-react";
import { useLanguage } from "../i18n";

import { BRAND_GROUPS, slug, loadSavedBrands, saveBrands } from "../formData";
import { BrandLogo } from "./FormExtras";

const BrandCard = ({ brand, id, selected, onToggle, t }) => (
  <div data-testid={`catalog-brand-${id}`} aria-checked={selected} role="checkbox"
    className={`group relative bg-white border rounded-sm px-4 pt-5 pb-3 min-h-[120px] flex flex-col items-center justify-between gap-3 text-center transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(42,39,38,0.08)] ${selected ? "border-[#C5A059] bg-[#C5A059]/10" : "border-ink/10 hover:border-[#C5A059]"}`}>
    {selected && <Check className="absolute top-2 left-2 w-3.5 h-3.5 text-[#C5A059]" strokeWidth={3} />}
    <BrandLogo name={brand.name} className="bw-hover" />
    <button type="button" data-testid={`catalog-add-${id}`} onClick={onToggle} aria-pressed={selected}
      className={`inline-flex items-center gap-1.5 h-7 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] rounded-sm border transition-colors ${selected ? "bg-[#C5A059] border-[#C5A059] text-white hover:bg-[#8A6A2B]" : "border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-cream"}`}>
      {selected ? <><Check className="w-3 h-3" strokeWidth={3} />{t.added}</> : <><Plus className="w-3 h-3" strokeWidth={2.5} />{t.add}</>}
    </button>
    {brand.url && (
      <a data-testid={`catalog-visit-${id}`} href={brand.url} target="_blank" rel="noopener noreferrer" aria-label={`${brand.name} — ${t.visit}`}
        className="absolute top-2 right-2 text-inksoft hover:text-[#C5A059] shrink-0"><ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></a>
    )}
  </div>
);

// bare: on the Brand page, whose cover already shows the title and intro
export const Catalogs = ({ bare = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(loadSavedBrands);
  const [q, setQ] = useState("");
  const c = t.catalogs;
  const ql = q.trim().toLowerCase();
  const groups = ql ? BRAND_GROUPS.map((g) => ({ ...g, brands: g.brands.filter((b) => b.name.toLowerCase().includes(ql)) })).filter((g) => g.brands.length) : BRAND_GROUPS;
  const goOfficial = () => window.open(`https://www.google.com/search?btnI=1&q=${encodeURIComponent(q.trim() + " illuminazione sito ufficiale")}`, "_blank", "noopener");

  const update = (next) => { setSelected(next); saveBrands(next); };
  const toggle = (name) => update(selected.includes(name) ? selected.filter((b) => b !== name) : [...selected, name]);

  return (
    <section id="cataloghi" data-testid="catalogs-section" className={`${bare ? "py-14 lg:py-20 bg-cream" : "py-24 lg:py-32 bg-sand/60"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {bare ? (
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-amberdark">{c.selectHint}</p>
        ) : (
          <motion.div className="max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-sagedark mb-6">{c.overline}</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight text-ink">{c.title}</h2>
            <p className="mt-5 text-base text-inksoft leading-relaxed">{c.subtitle}</p>
            <p className="mt-3 text-xs uppercase tracking-[0.15em] font-semibold text-amberdark">{c.selectHint}</p>
          </motion.div>
        )}

        <div className="mt-10 max-w-xl">
          <div className="flex items-center gap-3 bg-white border border-ink/15 rounded-sm px-4 h-12 focus-within:border-[#C5A059]">
            <Search className="w-4 h-4 text-inksoft" />
            <input data-testid="catalogs-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ql && !groups.length) goOfficial(); }} placeholder={c.searchPlaceholder} className="flex-1 bg-transparent text-sm outline-none" />
            {q && <button type="button" data-testid="catalogs-search-clear" onClick={() => setQ("")} className="text-inksoft hover:text-ink"><X className="w-4 h-4" /></button>}
          </div>
          {ql && !groups.length && (
            <div data-testid="catalogs-search-empty" className="mt-3 bg-white border border-[#C5A059]/50 rounded-sm p-4 text-sm text-ink flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="flex-1">{c.notFound.replace("{q}", q.trim())}</span>
              <button type="button" data-testid="catalogs-search-redirect" onClick={goOfficial} className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-[#C5A059] hover:text-ink rounded-sm px-4 h-10 text-[10px] font-semibold uppercase tracking-[0.15em]"><ExternalLink className="w-3.5 h-3.5" />{c.goOfficial}</button>
            </div>
          )}
        </div>

        {groups.map((g) => (
          <div key={g.key} id={`brand-group-${g.key}`} data-testid={`brand-group-${g.key}`} className={`scroll-mt-32 ${bare ? "mt-16 pt-10 border-t border-ink/10" : "mt-12"}`}>
            {bare ? (
              <div className="mb-6 flex items-baseline gap-5">
                <span className="font-display text-4xl lg:text-5xl text-[#C5A059] leading-none">{String(BRAND_GROUPS.findIndex((x) => x.key === g.key) + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-2xl lg:text-3xl tracking-tight text-ink">{g.label.split(" — ")[0]}</h3>
                  {g.label.includes(" — ") && <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-inksoft">{g.label.split(" — ").slice(1).join(" — ")}</p>}
                </div>
              </div>
            ) : (
              <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059] mb-4">{g.label}</h3>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {g.brands.map((brand) => {
                const id = `${g.key}-${slug(brand.name)}`;
                return <BrandCard key={id} brand={brand} id={id} selected={selected.includes(brand.name)} onToggle={() => toggle(brand.name)} t={c} />;
              })}
            </div>
          </div>
        ))}
        <p data-testid="catalogs-note" className="mt-8 text-xs text-inksoft/70 max-w-xl">{c.note}</p>
      </div>

      {selected.length > 0 && (
        <motion.div data-testid="catalogs-saved-bar" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }}
          className="fixed bottom-0 inset-x-0 z-40 bg-ink/95 backdrop-blur text-cream border-t border-[#C5A059]/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">{c.savedTitle}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selected.map((name) => (
                  <span key={name} data-testid={`catalogs-saved-${slug(name)}`} className="inline-flex items-center gap-1 text-[11px] bg-cream/10 border border-cream/15 rounded-sm px-2 py-1">
                    {name}
                    <button type="button" aria-label={`${c.remove} ${name}`} data-testid={`catalogs-remove-${slug(name)}`} onClick={() => toggle(name)} className="text-cream/60 hover:text-cream"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" data-testid="catalogs-clear" onClick={() => update([])} className="h-11 px-4 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream/70 hover:text-cream">{c.clear}</button>
              <button type="button" data-testid="catalogs-add-to-request" onClick={() => navigate("/richiedi-concept?plan=base#concept-block-brands")}
                className="inline-flex items-center gap-3 bg-[#C5A059] text-ink hover:bg-cream transition-colors rounded-sm px-6 h-11 text-xs font-semibold uppercase tracking-[0.15em] whitespace-nowrap">
                <Send className="w-4 h-4" strokeWidth={1.5} />
                {c.addToRequest} ({selected.length})
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
};
